// ============================================================
// TMR Portal Admin — formulário de taxa ↔ payload
// ============================================================
import type {
  ApplyTemporaryRateRequest,
  CompanyRate,
  CreateRateRequest,
  UpdateRateRequest,
} from './contract';

/** The rate form, all strings — a form field holds text until it is validated. */
export interface RateForm {
  /** Empty for a Taxa da Empresa; the legacy product id for a Taxa do Lançamento. */
  productId: string;
  rateTypeId: string;
  creditEngineTypeId: string;
  minInstallment: string;
  maxInstallment: string;
  standardValue: string;
}

/** The temporary-rate form. */
export interface TemporaryRateForm {
  value: string;
  startDate: string;
  endDate: string;
  reason: string;
  cascade: boolean;
}

/** A blank rate form, defaulted to the common case: a Taxa da Empresa covering one to twelve parcels. */
export function emptyRateForm(): RateForm {
  return {
    productId: '',
    rateTypeId: '',
    creditEngineTypeId: '',
    minInstallment: '1',
    maxInstallment: '12',
    standardValue: '',
  };
}

/**
 * The form as it starts when editing an existing rate.
 *
 * @param rate The rate being edited.
 */
export function toRateForm(rate: CompanyRate): RateForm {
  return {
    productId: rate.productId == null ? '' : String(rate.productId),
    rateTypeId: String(rate.rateTypeId),
    creditEngineTypeId: rate.creditEngineTypeId == null ? '' : String(rate.creditEngineTypeId),
    minInstallment: String(rate.minInstallment),
    maxInstallment: String(rate.maxInstallment),
    standardValue: formatDecimal(rate.standardValue),
  };
}

/** Field-level messages, keyed by form field. Empty means the form may be submitted. */
export type RateFormErrors = Partial<Record<keyof RateForm, string>>;

/**
 * Validates the rate form.
 *
 * It deliberately does **not** check band overlap: that depends on every other rate of the Empresa and on
 * how the server pairs them, and a second implementation here would eventually disagree with the one that
 * decides. The server answers `company.rate_overlap`, and the modal shows it.
 *
 * @param form The form as typed.
 */
export function validateRateForm(form: RateForm): RateFormErrors {
  const errors: RateFormErrors = {};

  if (!form.rateTypeId) errors.rateTypeId = 'Escolha o tipo da taxa';

  const min = parseInteger(form.minInstallment);
  const max = parseInteger(form.maxInstallment);

  if (min == null || min < 0) errors.minInstallment = 'Informe a parcela inicial';
  if (max == null || max < 0) errors.maxInstallment = 'Informe a parcela final';
  if (min != null && max != null && max < min) {
    errors.maxInstallment = 'A parcela final não pode ser menor que a inicial';
  }

  const value = parseDecimal(form.standardValue);
  if (value == null) errors.standardValue = 'Informe o valor da taxa';
  else if (value < 0) errors.standardValue = 'O valor não pode ser negativo';

  if (form.productId && parseInteger(form.productId) == null) {
    errors.productId = 'O produto deve ser um número';
  }

  return errors;
}

/**
 * The create payload. Call only on a form {@link validateRateForm} accepted.
 *
 * @param form The validated form.
 */
export function toCreateRequest(form: RateForm): CreateRateRequest {
  return {
    productId: form.productId ? parseInteger(form.productId) : null,
    rateTypeId: parseInteger(form.rateTypeId)!,
    creditEngineTypeId: form.creditEngineTypeId ? parseInteger(form.creditEngineTypeId) : null,
    minInstallment: parseInteger(form.minInstallment)!,
    maxInstallment: parseInteger(form.maxInstallment)!,
    standardValue: parseDecimal(form.standardValue)!,
    // Carried, not offered: the legacy's tipo_valor_id has no meaning anybody has written down, so the
    // form has no field for it and a new rate leaves it empty (08 §5).
    rateValueKindId: null,
  };
}

/**
 * The update payload — the create one without the scope, which an edit cannot change.
 *
 * @param form The validated form.
 */
export function toUpdateRequest(form: RateForm): UpdateRateRequest {
  const { productId: _productId, ...rest } = toCreateRequest(form);
  return rest;
}

/** Field-level messages for the temporary-rate form. */
export type TemporaryRateFormErrors = Partial<Record<keyof TemporaryRateForm, string>>;

/**
 * Validates the temporary-rate form.
 *
 * A window that starts in the past is allowed on purpose: recording an exception that was agreed last week
 * is ordinary, and the effective value simply applies from today onwards.
 *
 * @param form The form as typed.
 */
export function validateTemporaryRateForm(form: TemporaryRateForm): TemporaryRateFormErrors {
  const errors: TemporaryRateFormErrors = {};

  const value = parseDecimal(form.value);
  if (value == null) errors.value = 'Informe o valor da taxa temporária';
  else if (value < 0) errors.value = 'O valor não pode ser negativo';

  if (!form.startDate) errors.startDate = 'Informe a data de início';
  if (!form.endDate) errors.endDate = 'Informe a data de término';
  if (form.startDate && form.endDate && form.endDate < form.startDate) {
    errors.endDate = 'O término não pode ser anterior ao início';
  }

  return errors;
}

/**
 * The apply payload. Call only on a form {@link validateTemporaryRateForm} accepted.
 *
 * @param form The validated form.
 */
export function toApplyTemporaryRequest(form: TemporaryRateForm): ApplyTemporaryRateRequest {
  return {
    value: parseDecimal(form.value)!,
    startDate: form.startDate,
    endDate: form.endDate,
    reason: form.reason.trim() || null,
    cascadeToLaunchRates: form.cascade,
  };
}

/**
 * A decimal as typed by a Brazilian operator — comma or dot, either accepted.
 *
 * Returns null for anything that is not a number, which is what the validators branch on.
 *
 * @param raw The typed text.
 */
export function parseDecimal(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A rate value as it is shown in a field: two decimals, comma-separated.
 *
 * @param value The numeric value.
 */
export function formatDecimal(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/**
 * An integer as typed. Null for anything else, including a decimal — an installment count is whole.
 *
 * @param raw The typed text.
 */
function parseInteger(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}
